import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const CompanySetup = ({ data, onUpdate, errors, setErrors }) => {
  // Helper functions for auto-generating codes
  const generateCompanyCode = (companyName) => {
    if (!companyName.trim()) return 'COMP';

    // Get first letter of each word, up to 3 letters
    const words = companyName.trim().split(/\s+/);
    const acronym = words
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 3);

    // Get current date in MMDDYY format
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const dateStr = `${month}${day}${year}`;

    return `${acronym}${dateStr}`;
  };

  const generateBranchCode = (branchName) => {
    if (!branchName.trim()) return 'BRN';

    // Get first letter of each word, up to 2 letters
    const words = branchName.trim().split(/\s+/);
    const acronym = words
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    // Get current date and time in MMDDYYHHMM format
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const dateTimeStr = `${month}${day}${year}${hours}${minutes}`;

    return `${acronym}${dateTimeStr}`;
  };

  // Country to currency mapping
  const countryCurrencyMap = {
    'US': 'USD',
    'CA': 'CAD',
    'GB': 'GBP',
    'AU': 'AUD',
    'DE': 'EUR',
    'FR': 'EUR',
    'IN': 'INR',
    'SG': 'SGD',
    'AE': 'AED',
    'BR': 'BRL',
    'MX': 'MXN',
    'ZA': 'ZAR',
    'NG': 'NGN',
    'KE': 'KES',
    'EG': 'EGP'
  };

  const [companyData, setCompanyData] = useState(() => {
    const initialData = {
      companyName: data.companyName || '',
      companyCode: data.companyCode || (data.companyName ? generateCompanyCode(data.companyName) : ''),
      companyAddress: data.companyAddress || {
        street: '',
        city: '',
        state: '',
        country: 'United States',
        zipCode: ''
      },
      companyPhone: data.companyPhone || '',
      companyEmail: data.companyEmail || '',
      countryCode: data.countryCode || '',
      currency: data.currency || '',
      timezone: data.timezone || ''
    };
    return initialData;
  });

  const [branchData, setBranchData] = useState(() => {
    const initialData = {
      branchName: data.branchName || '',
      branchCode: data.branchCode || (data.branchName ? generateBranchCode(data.branchName) : ''),
      branchAddress: data.branchAddress || {
        street: '',
        city: '',
        state: '',
        country: 'United States',
        zipCode: ''
      }
    };
    return initialData;
  });

  // Auto-update parent with initial auto-generated codes
  useEffect(() => {
    const initialUpdate = {
      ...companyData,
      ...branchData
    };
    onUpdate(initialUpdate);
  }, []); // Empty dependency array to run only on mount

  const handleCompanyChange = (field, value) => {
    console.log('CompanySetup - handleCompanyChange:', field, value);
    const updatedData = { ...companyData, [field]: value };

    // Auto-generate company code when company name changes
    if (field === 'companyName' && value.trim()) {
      const generatedCode = generateCompanyCode(value);
      updatedData.companyCode = generatedCode;
      console.log('CompanySetup - Company code auto-generated:', generatedCode);
    }

    // Auto-update currency when country changes
    if (field === 'countryCode') {
      updatedData.currency = countryCurrencyMap[value] || 'USD';
      console.log('CompanySetup - Currency updated to:', updatedData.currency);
    }

    setCompanyData(updatedData);
    onUpdate(updatedData);

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCompanyAddressChange = (field, value) => {
    const updatedAddress = { ...companyData.companyAddress, [field]: value };
    const updatedData = { ...companyData, companyAddress: updatedAddress };
    setCompanyData(updatedData);

    // Auto-copy company address to branch address (only if branch address is empty or same as company)
    const currentBranchAddress = branchData.branchAddress;
    const isBranchAddressEmpty = !currentBranchAddress.street && !currentBranchAddress.city &&
                                !currentBranchAddress.state && !currentBranchAddress.zipCode;
    const isBranchAddressSame = currentBranchAddress.street === companyData.companyAddress.street &&
                               currentBranchAddress.city === companyData.companyAddress.city &&
                               currentBranchAddress.state === companyData.companyAddress.state &&
                               currentBranchAddress.country === companyData.companyAddress.country &&
                               currentBranchAddress.zipCode === companyData.companyAddress.zipCode;

    if (isBranchAddressEmpty || isBranchAddressSame) {
      const updatedBranchData = {
        ...branchData,
        branchAddress: { ...updatedAddress }
      };
      setBranchData(updatedBranchData);
      onUpdate({ ...updatedData, ...updatedBranchData });
    } else {
      onUpdate(updatedData);
    }
  };

  const handleBranchChange = (field, value) => {
    const updatedData = { ...branchData, [field]: value };

    // Auto-generate branch code when branch name changes
    if (field === 'branchName' && value.trim()) {
      const generatedCode = generateBranchCode(value);
      updatedData.branchCode = generatedCode;
      console.log('CompanySetup - Branch code auto-generated:', generatedCode);
    }

    setBranchData(updatedData);
    onUpdate(updatedData);

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBranchAddressChange = (field, value) => {
    const updatedAddress = { ...branchData.branchAddress, [field]: value };
    const updatedData = { ...branchData, branchAddress: updatedAddress };
    setBranchData(updatedData);
    onUpdate(updatedData);
  };

  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IN', name: 'India' },
    { code: 'SG', name: 'Singapore' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'BR', name: 'Brazil' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'JP', name: 'Japan' }
  ];

  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' }
  ];

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)' },
    { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Set Up Your Company
        </h2>
        <p className="text-lg text-gray-600">
          Tell us about your organization and create your first branch
        </p>
      </div>

      <div className="space-y-8">
        {/* Company Information */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
          <div className="flex items-center mb-6">
            <BuildingOfficeIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-900">Company Information</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                value={companyData.companyName}
                onChange={(e) => handleCompanyChange('companyName', e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                  errors.companyName ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter company name"
              />
              {errors.companyName && (
                <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Code *
                <span className="text-xs text-blue-600 ml-2">(Auto-generated from company name)</span>
              </label>
              <input
                type="text"
                value={companyData.companyCode}
                onChange={(e) => handleCompanyChange('companyCode', e.target.value.toUpperCase())}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                  errors.companyCode ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., ABC_CORP"
                maxLength={20}
              />
              {errors.companyCode && (
                <p className="mt-1 text-sm text-red-600">{errors.companyCode}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <PhoneIcon className="h-4 w-4 inline mr-1" />
                Phone Number
              </label>
              <input
                type="tel"
                value={companyData.companyPhone}
                onChange={(e) => handleCompanyChange('companyPhone', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="+1 (555) 123-4567"
              />
              {errors.companyPhone && (
                <p className="mt-1 text-sm text-red-600">{errors.companyPhone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                Email Address
              </label>
              <input
                type="email"
                value={companyData.companyEmail}
                onChange={(e) => handleCompanyChange('companyEmail', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="contact@company.com"
              />
              {errors.companyEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.companyEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                Country
              </label>
              <select
                value={companyData.countryCode}
                onChange={(e) => handleCompanyChange('countryCode', e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                  errors.countryCode ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select your country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.countryCode && (
                <p className="mt-1 text-sm text-red-600">{errors.countryCode}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
                <span className="text-xs text-blue-600 ml-2">(Auto-updated based on country)</span>
              </label>
              <div className="relative">
                <select
                  value={companyData.currency}
                  onChange={(e) => handleCompanyChange('currency', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                >
                  {currencies.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.symbol} {currency.name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-3 text-xs text-gray-500">
                  {companyData.currency}
                </div>
              </div>
              {errors.currency && (
                <p className="mt-1 text-sm text-red-600">{errors.currency}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <select
                value={companyData.timezone}
                onChange={(e) => handleCompanyChange('timezone', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Company Address */}
          <div className="mt-6">
            <div className="flex items-center mb-4">
              <MapPinIcon className="h-5 w-5 text-blue-600 mr-2" />
              <h4 className="text-lg font-medium text-gray-900">Company Address</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={companyData.companyAddress.street}
                  onChange={(e) => handleCompanyAddressChange('street', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Street address"
                />
              </div>

              <input
                type="text"
                value={companyData.companyAddress.city}
                onChange={(e) => handleCompanyAddressChange('city', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="City"
              />

              <input
                type="text"
                value={companyData.companyAddress.state}
                onChange={(e) => handleCompanyAddressChange('state', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="State/Province"
              />

              <input
                type="text"
                value={companyData.companyAddress.zipCode}
                onChange={(e) => handleCompanyAddressChange('zipCode', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="ZIP/Postal code"
              />

              <select
                value={companyData.companyAddress.country}
                onChange={(e) => handleCompanyAddressChange('country', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Branch Information */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
          <div className="flex items-center mb-6">
            <BuildingOfficeIcon className="h-6 w-6 text-green-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-900">Head Office Branch</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch Name *
              </label>
              <input
                type="text"
                value={branchData.branchName}
                onChange={(e) => handleBranchChange('branchName', e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                  errors.branchName ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Headquarters"
              />
              {errors.branchName && (
                <p className="mt-1 text-sm text-red-600">{errors.branchName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch Code *
                <span className="text-xs text-green-600 ml-2">(Auto-generated from branch name)</span>
              </label>
              <input
                type="text"
                value={branchData.branchCode}
                onChange={(e) => handleBranchChange('branchCode', e.target.value.toUpperCase())}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 ${
                  errors.branchCode ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., HQ001"
                maxLength={10}
              />
              {errors.branchCode && (
                <p className="mt-1 text-sm text-red-600">{errors.branchCode}</p>
              )}
            </div>
          </div>

          {/* Branch Address */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-green-600 mr-2" />
                <h4 className="text-lg font-medium text-gray-900">Branch Address</h4>
              </div>
              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                Auto-copied from company address
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={branchData.branchAddress.street}
                  onChange={(e) => handleBranchAddressChange('street', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Street address"
                />
              </div>

              <input
                type="text"
                value={branchData.branchAddress.city}
                onChange={(e) => handleBranchAddressChange('city', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder="City"
              />

              <input
                type="text"
                value={branchData.branchAddress.state}
                onChange={(e) => handleBranchAddressChange('state', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder="State/Province"
              />

              <input
                type="text"
                value={branchData.branchAddress.zipCode}
                onChange={(e) => handleBranchAddressChange('zipCode', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder="ZIP/Postal code"
              />

              <select
                value={branchData.branchAddress.country}
                onChange={(e) => handleBranchAddressChange('country', e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySetup;
